<script setup lang="ts">
/**
 * 应用根组件
 *
 * 两类布局：
 *   meta.bare  → 裸页（登录 / 扫码回调），不带侧栏与顶栏
 *   其余        → AppShell（侧栏 + 顶栏 + 内容区，内部含 RouterView）
 *
 * ★ 合规公示页脚挂在这里而不是 AppShell 里：
 *   裸页（登录页）也必须能看到备案信息 —— 法规要求公示对**未登录公众**可见，
 *   放进入口后的外壳里就等于藏起来了。
 */
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import AppShell from '@/layouts/AppShell.vue';
import ComplianceFooter from '@/components/ComplianceFooter.vue';
import { useAuthStore } from '@/stores/auth';

const route = useRoute();
const auth = useAuthStore();

const bare = computed(() => route.meta.bare === true);

/**
 * ★ 必须**在 setup 里**就开始拉会话，而不是等到 onMounted。
 *
 * onMounted 是子组件先于父组件触发的：AppShell 和它里面的 RouterView 子页面
 * 会**先挂载**，那时 bootstrap 还没开始跑 —— 而「当前生效的生意档案」
 * （`auth.businessId`）正是 bootstrap 里异步拿到的。
 * 于是每个"进门就按 businessId 拉数据"的页面（选题与脚本、交付成果……）
 * 首屏都会拉到空：新会话第一次进「选题与脚本」会看到**「还没有选题」**，
 * 而选题其实是有的（还会顺手引导用户再生成一批、白花积分）。
 *
 * 配合 AppShell 里 `v-if="auth.ready"` 的门，子页面挂载时档案一定已经就位。
 * 这里只是让它早一点开始跑，少一段白等。
 */
void auth.bootstrap();
</script>

<template>
  <!-- 裸页（登录等）：内容 + 公示页脚，页脚保证未登录也能看到 -->
  <div v-if="bare" class="bare-wrap">
    <RouterView />
    <ComplianceFooter />
  </div>
  <AppShell v-else />
</template>

<style scoped>
.bare-wrap {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}
.bare-wrap > :first-child {
  flex: 1;
}
</style>
